import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import {
  Button as MuiButton,
  Card as MuiCard,
  Grid as MuiGrid,
  CardContent as MuiCardContent,
  TextField as MuiTextField,
  CardHeader,
  MenuItem,
  Divider as MuiDivider,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { spacing } from '@mui/system'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'

import { getMyProjects, getProjects, setProject } from '../../redux/slices/projects'
import useAuth from '../../hooks/useAuth'
import { RootState } from '../../redux'
import { createErrorOrSuccessNotification } from '../../redux/slices/notifications'
import AddIcon from '@mui/icons-material/Add'
import { setData, setStep } from '../../redux/slices/steps'
import { setDevice } from '../../redux/slices/devices'
import { QRData } from '../../enums/QRData'
import DeviceInstallProjectCreation from './DeviceInstallProjectCreation'
import { NotificationType } from '../../enums/NotificationType'
import { ProjectReducerType } from '../../enums/ProjectReducerType'
import { createRequest } from '../../services/requestService'
import { RequestableType } from '../../enums/RequestableType'
import ProjectMembershipRole from '../../enums/ProjectMembershipRole'
import { createUserNotification } from '../../utils/createUserNotification'
import { RequestAccessErrors } from '../../enums/RequestAccessErrors'
import { RequesterType } from '../../enums/RequesterType'
import { Project } from '../../types'

/**
 * The styled TextField
 */
const TextField = styled(MuiTextField)<{ my?: number }>(spacing)

/**
 * The styled Button
 */
const Button = styled(MuiButton)(spacing)

/**
 * The styled Card
 */
const Card = styled(MuiCard)(spacing)

/**
 * The styled Grid
 */
const Grid = styled(MuiGrid)(spacing)

/**
 * The styled CardContent
 */
const CardContent = styled(MuiCardContent)(spacing)

/**
 * The styled Divider
 */
const Divider = styled(MuiDivider)(spacing)

/**
 * The properties
 */
interface DeviceInstallProjectFormProps {
  projectSelected(selectedProject: number): void
}

export default function SelectProjectForm({ projectSelected }: DeviceInstallProjectFormProps) {
  const dispatch = useDispatch()

  const [t] = useTranslation('common')
  /**
   * Current user
   */
  const { currentUser } = useAuth()
  /**
   * User's active projects
   */
  const activeProjects = useSelector(
    (state: RootState) => state.projects.activeProjects
  ) as Project[]

  /**
   * The active projects.
   */
  const { projects } = useSelector((state: RootState) => state.projects)

  const [isUsingNearby, setIsUsingNearby] = useState(false)

  /**
   * User wants to create new project base
   */
  const [createNewProject, setCreateNewProject] = useState<boolean>(false)

  /**
   * All of the projects that are shown. Filtered so that there are no duplicates.
   */
  const allProjects = Array.from(new Set(activeProjects.concat(isUsingNearby ? projects : [])))

  /**
   * Initial values
   */
  const initialValues = {
    project: ''
  }

  /**
   * Navigator options
   */
  const options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0
  }

  useEffect(() => {
    dispatch(setDevice(undefined))
    dispatch(setData(QRData.NOT_SCANNED))
    dispatch(setProject(undefined))
    dispatch(setStep(0))
    dispatch(getMyProjects(ProjectReducerType.ACTIVE_PROJECTS))
  }, [currentUser])

  /**
   * Handle user's current location
   */
  function handleLocationClick() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error, options)
    } else {
      dispatch(
        createErrorOrSuccessNotification(NotificationType.WARNING, t('Manufacturing.notSupported'))
      )
    }
  }

  /**
   * Location handling success
   * Fetch projects by user's current location
   * @param position
   */
  function success(position: { coords: { latitude: number; longitude: number } }) {
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    dispatch(getProjects({ showDeleted: false, lat: latitude, lon: longitude }))
  }

  /**
   * Location handling error
   */
  function error() {
    dispatch(
      createErrorOrSuccessNotification(
        NotificationType.WARNING,
        t('Manufacturing.locationNotFound')
      )
    )
  }

  /**
   * Checkbox handler
   * If checked try to fetch user's current location
   * @param event
   */
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    setIsUsingNearby(isChecked)
    if (isChecked) {
      handleLocationClick()
    } else {
      clearSelectedProject()
    }
  }

  const [selectedProject, setSelectedProject] = useState<string>('')

  const clearSelectedProject = () => {
    setSelectedProject('')
  }

  /**
   * Handle submit
   * @param values
   */
  const handleSubmit = async (
    _values: { project: string },
    { resetForm }: FormikHelpers<{ project: string }>
  ) => {
    if (currentUser === null) {
      return
    }

    if (!selectedProject) {
      return
    }

    try {
      if (isUsingNearby) {
        await createRequest({
          requesterType: RequesterType.USER,
          requesterId: currentUser.id.toString(),
          requestableType: RequestableType.PROJECT,
          requestableId: selectedProject,
          role: ProjectMembershipRole.USER,
          message: null
        })
        dispatch(
          createErrorOrSuccessNotification(NotificationType.SUCCESS, t('requestCreateSuccess'))
        )
      } else {
        projectSelected(parseInt(selectedProject))
      }
      resetForm()
    } catch (error: unknown) {
      //Create message
      const errorMessage = createUserNotification({
        user: currentUser,
        type: RequestAccessErrors.CREATE,
        error: error
      })

      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )
    }
  }

  return (
    <React.Fragment>
      {createNewProject ? (
        /**
         * Project base creation
         */
        <DeviceInstallProjectCreation
          afterCreate={(project) => {
            projectSelected(project.id)
          }}
          onCancel={() => {
            setCreateNewProject(false)
          }}
        />
      ) : (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
          {({ errors, handleBlur, handleSubmit, touched }) => (
            <Card mb={6}>
              <CardContent>
                <Grid container spacing={6}>
                  <Grid item xs={6}>
                    <Typography align="left">
                      <Button
                        size="small"
                        onClick={() => {
                          setCreateNewProject(true)
                        }}
                        variant="contained"
                        color="success"
                        mt={3}
                      >
                        {t('Manufacturing.createNewProject')}
                        <AddIcon />
                      </Button>
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 6 }} />
                <form onSubmit={handleSubmit}>
                  <>
                    <CardHeader
                      title={t('Manufacturing.chooseActiveProject')}
                      align="center"
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <Grid container spacing={6}>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl>
                        <TextField
                          name="project"
                          label={t('Manufacturing.yourProjects')}
                          select
                          value={selectedProject}
                          error={Boolean(touched.project && errors.project)}
                          fullWidth
                          helperText={touched.project && errors.project}
                          onBlur={handleBlur}
                          onChange={(e) => setSelectedProject(e.target.value)}
                          type="text"
                          variant="outlined"
                          my={2}
                        >
                          {allProjects.map((project: Project) => (
                            <MenuItem key={project.id} value={project.id}>
                              {project.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </>

                  <FormControlLabel
                    control={<Checkbox onChange={handleCheckboxChange} />}
                    label={t('Manufacturing.wantsNearbyProjects') as string}
                  />

                  <Divider sx={{ my: 6 }} />

                  <Typography align="right">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      mt={3}
                      disabled={!selectedProject}
                    >
                      {t('Manufacturing.next')}
                    </Button>
                  </Typography>
                </form>
              </CardContent>
            </Card>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
